import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../contexts/TranslationContext';
import { Resource, UIComponent } from '../../types';

interface TenFrameComponentProps {
  resource: Resource;
  component: UIComponent;
  onAction?: (action: string, data?: any) => void;
}

export const TenFrameComponent: React.FC<TenFrameComponentProps> = ({ resource, component, onAction }) => {
  const { t } = useTranslation();
  const [counters, setCounters] = useState<boolean[][]>(
    Array(resource.rows || 2).fill(null).map(() => Array(resource.cols || 5).fill(false))
  );
  const [presetValue, setPresetValue] = useState<number>(0);

  // Reset component state when resource changes (for quiz navigation)
  useEffect(() => {
    const newRows = resource.rows || 2;
    const newCols = resource.cols || 5;
    setCounters(Array(newRows).fill(null).map(() => Array(newCols).fill(false)));
    setPresetValue(0);
  }, [resource.id, resource.rows, resource.cols]);

  const handleCounterClick = (row: number, col: number) => {
    const newCounters = counters.map((r, ri) =>
      r.map((c, ci) => ri === row && ci === col ? !c : c)
    );
    setCounters(newCounters);
    const totalCount = newCounters.flat().filter(Boolean).length;
    onAction?.('counter_clicked', { row, col, total: totalCount });
  };

  const handlePreset = (value: number) => {
    setPresetValue(value);
    const newCounters = counters.map(row => row.map(() => false));
    let remaining = value;
    
    for (let row = 0; row < newCounters.length && remaining > 0; row++) {
      for (let col = 0; col < newCounters[row].length && remaining > 0; col++) {
        newCounters[row][col] = true;
        remaining--;
      }
    }
    
    setCounters(newCounters);
    onAction?.('preset_applied', { value });
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-900">{t('tenFrame.tenFrame')}</h3>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handlePreset(10)}
            className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
          >
            10
          </button>
          <button
            onClick={() => handlePreset(15)}
            className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
          >
            20
          </button>
        </div>
      </div>
      
      <div className="space-y-2">
        {counters.map((row, rowIndex) => (
          <div key={rowIndex} className="flex space-x-2">
            {row.map((counter, colIndex) => (
              <button
                key={colIndex}
                onClick={() => handleCounterClick(rowIndex, colIndex)}
                className={`w-8 h-8 rounded border-2 transition-all ${
                  counter
                    ? 'bg-blue-500 border-blue-600'
                    : 'bg-gray-100 border-gray-300 hover:border-gray-400'
                }`}
              />
            ))}
          </div>
        ))}
      </div>
      
      <div className="mt-3 text-sm text-gray-600">
        {t('tenFrame.count')}: {counters.flat().filter(Boolean).length} | {t('tenFrame.preset')}: {presetValue}
      </div>
    </div>
  );
};
